import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BookOpen, Search, Heart, Apple, Leaf, 
  Calendar, ExternalLink, Clock, Filter, ChevronRight,
  Loader2, Utensils, Star, Beaker
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const Education = () => {
  const { API } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Articles
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Recipes
  const [recipes, setRecipes] = useState([]);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [selectedCondition, setSelectedCondition] = useState('');
  const [selectedDiet, setSelectedDiet] = useState('');
  
  // Seasonal
  const [seasonalData, setSeasonalData] = useState(null);
  
  // Health Guidelines
  const [guidelines, setGuidelines] = useState(null);
  
  // My Foods Education (NEW)
  const [myFoodsEducation, setMyFoodsEducation] = useState(null);
  const [loadingMyFoods, setLoadingMyFoods] = useState(false);

  useEffect(() => {
    // Use fallback data directly since backend endpoints don't exist yet
    setArticles(getFallbackArticles());
    setSeasonalData(getFallbackSeasonalData());
    setMyFoodsEducation({ has_meal_plan: false, message: 'Generează un plan alimentar pentru a vedea informații personalizate' });
  }, []);
  
  const fetchMyFoodsEducation = async () => {
    setLoadingMyFoods(true);
    try {
      const response = await axios.get(`${API}/education/my-foods`);
      setMyFoodsEducation(response.data);
    } catch (error) {
      // Silently handle - endpoint may not exist yet
      setMyFoodsEducation({ has_meal_plan: false, message: 'Funcționalitate în dezvoltare' });
    } finally {
      setLoadingMyFoods(false);
    }
  };

  const fetchArticles = async (category = null) => {
    setLoading(true);
    try {
      const params = category && category !== 'all' ? `?category=${category}` : '';
      const response = await axios.get(`${API}/education/articles${params}`);
      setArticles(response.data.articles || []);
    } catch (error) {
      // Use fallback data - endpoint may not exist yet
      setArticles(getFallbackArticles());
    } finally {
      setLoading(false);
    }
  };

  const fetchSeasonalIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients/seasonal`);
      setSeasonalData(response.data);
    } catch (error) {
      // Silently handle - use fallback seasonal data
      setSeasonalData(getFallbackSeasonalData());
    }
  };
  
  const getFallbackSeasonalData = () => {
    const month = new Date().getMonth();
    const monthNames = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
                        'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
    return {
      month_name: monthNames[month],
      fruits: ['mere', 'pere', 'portocale', 'mandarine', 'kiwi'],
      vegetables: ['varză', 'morcovi', 'cartofi', 'ceapă', 'usturoi', 'sfeclă'],
      proteins: ['pui', 'curcan', 'porc', 'vită', 'pește'],
      tips: 'Alege produse locale și de sezon pentru cel mai bun raport calitate-preț și cea mai mare valoare nutritivă.'
    };
  };

  const searchRecipes = async (e) => {
    e?.preventDefault();
    if (!recipeSearch.trim()) return;
    
    setLoading(true);
    try {
      let url = `${API}/recipes/search?query=${encodeURIComponent(recipeSearch)}`;
      if (selectedDiet) url += `&diet=${selectedDiet}`;
      if (selectedCondition) url += `&health_condition=${selectedCondition}`;
      
      const response = await axios.get(url);
      setRecipes(response.data.recipes || []);
      
      if (response.data.recipes?.length === 0) {
        toast.info('Nu am găsit rețete. Încearcă alte cuvinte cheie.');
      }
    } catch (error) {
      console.error('Error searching recipes:', error);
      toast.error('Eroare la căutarea rețetelor');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipesByCondition = async (condition) => {
    setLoading(true);
    setSelectedCondition(condition);
    try {
      const response = await axios.get(`${API}/recipes/condition/${condition}`);
      setRecipes(response.data.recipes || []);
      setGuidelines(response.data.guidelines);
      toast.success(`Afișez rețete pentru ${condition}`);
    } catch (error) {
      console.error('Error fetching condition recipes:', error);
      toast.error('Eroare la încărcarea rețetelor');
    } finally {
      setLoading(false);
    }
  };

  const getFallbackArticles = () => [
    {
      id: 'art-001',
      title: 'Ghid Complet pentru Dieta Mediteraneană',
      summary: 'Beneficiile dovedite științific ale dietei mediteraneene pentru sănătatea cardiovasculară.',
      category: 'nutrition',
      source: 'European Society of Cardiology',
      image: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=400',
      read_time: 8,
      topics: ['dieta mediteraneana', 'sanatate cardiaca']
    },
    {
      id: 'art-002',
      title: 'Controlul Glicemiei prin Alimentație',
      summary: 'Cum să alegi alimentele potrivite pentru menținerea nivelului optim de zahăr în sânge.',
      category: 'conditions',
      source: 'American Diabetes Association',
      image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400',
      read_time: 10,
      topics: ['diabet', 'glicemie']
    },
    {
      id: 'art-003',
      title: 'Dieta DASH pentru Hipertensiune',
      summary: 'Planul alimentar DASH dezvoltat de NIH pentru reducerea tensiunii arteriale.',
      category: 'conditions',
      source: 'NIH - NHLBI',
      image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400',
      read_time: 12,
      topics: ['hipertensiune', 'dash']
    }
  ];

  const categories = [
    { id: 'all', label: 'Toate', icon: BookOpen },
    { id: 'nutrition', label: 'Nutriție', icon: Apple },
    { id: 'conditions', label: 'Afecțiuni', icon: Heart },
    { id: 'lifestyle', label: 'Stil de Viață', icon: Leaf }
  ];

  const healthConditions = [
    { id: 'diabetes', label: 'Diabet', color: 'bg-blue-500' },
    { id: 'heart-disease', label: 'Boli Cardiace', color: 'bg-red-500' },
    { id: 'hypertension', label: 'Hipertensiune', color: 'bg-purple-500' },
    { id: 'obesity', label: 'Obezitate', color: 'bg-orange-500' },
    { id: 'kidney-disease', label: 'Boli Renale', color: 'bg-teal-500' }
  ];

  const dietTypes = [
    { id: '', label: 'Toate' },
    { id: 'vegetarian', label: 'Vegetarian' },
    { id: 'vegan', label: 'Vegan' },
    { id: 'gluten-free', label: 'Fără Gluten' },
    { id: 'low-carb', label: 'Low Carb' }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-6 md:px-12 py-8">
        <div className="animate-fade-in">
          <div className="text-center mb-8">
            <h1 className="font-serif text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
              Educație Nutrițională
            </h1>
            <p className="text-[#4a5568] text-lg">
              Articole, rețete și ghiduri bazate pe studii științifice
            </p>
            <div className="flex items-center justify-center gap-2 mt-2 text-sm text-[#4a5568]">
              <span>Surse:</span>
              <Badge variant="outline">USDA</Badge>
              <Badge variant="outline">NIH</Badge>
              <Badge variant="outline">OMS</Badge>
              <Badge variant="outline">ESC</Badge>
            </div>
          </div>

          <Tabs defaultValue="my-foods" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-5 mb-8">
              <TabsTrigger value="my-foods" className="text-xs sm:text-sm">
                <Utensils className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Alimentele Mele</span>
                <span className="sm:hidden">Ale Mele</span>
              </TabsTrigger>
              <TabsTrigger value="articles" className="text-xs sm:text-sm">
                <BookOpen className="w-4 h-4 mr-1 sm:mr-2" />
                Articole
              </TabsTrigger>
              <TabsTrigger value="recipes" className="text-xs sm:text-sm">
                <Apple className="w-4 h-4 mr-1 sm:mr-2" />
                Rețete
              </TabsTrigger>
              <TabsTrigger value="conditions" className="text-xs sm:text-sm">
                <Heart className="w-4 h-4 mr-1 sm:mr-2" />
                Afecțiuni
              </TabsTrigger>
              <TabsTrigger value="seasonal" className="text-xs sm:text-sm">
                <Calendar className="w-4 h-4 mr-1 sm:mr-2" />
                Sezonier
              </TabsTrigger>
            </TabsList>

            {/* My Foods Tab - Personalized Education */}
            <TabsContent value="my-foods">
              {loadingMyFoods ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
                </div>
              ) : myFoodsEducation?.has_meal_plan ? (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="font-serif text-2xl text-[#1A4D2E] mb-2">
                      Alimentele din Planul Tău Alimentar
                    </h2>
                    <p className="text-[#4a5568]">
                      Descoperă de ce aceste alimente sunt benefice pentru tine
                    </p>
                    {myFoodsEducation.medical_conditions?.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-2 mt-3">
                        <span className="text-sm text-[#4a5568]">Afecțiunile tale:</span>
                        {myFoodsEducation.medical_conditions.map((cond, i) => (
                          <Badge key={i} className="bg-[#1A4D2E] text-white">{cond}</Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Therapeutic Foods Section */}
                  {myFoodsEducation.therapeutic_foods?.length > 0 && (
                    <Card className="border-none card-shadow border-l-4 border-l-[#9BCF53] mb-6">
                      <CardHeader>
                        <CardTitle className="font-serif text-xl text-[#1A4D2E] flex items-center gap-2">
                          <Star className="w-5 h-5 text-amber-500" />
                          Alimente Terapeutice din Planul Tău
                        </CardTitle>
                        <CardDescription>
                          Aceste alimente au fost selectate special pentru beneficiile lor medicale
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-4">
                          {myFoodsEducation.therapeutic_foods.map((food, i) => (
                            <div key={i} className="p-4 bg-[#F5F5F0] rounded-xl">
                              <h4 className="font-medium text-[#1A4D2E] mb-2">{food.food}</h4>
                              <p className="text-sm text-[#4a5568] mb-2">{food.medical_benefit}</p>
                              {food.conditions_helped?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {food.conditions_helped.map((c, j) => (
                                    <Badge key={j} className="bg-green-100 text-green-700 text-xs">{c}</Badge>
                                  ))}
                                </div>
                              )}
                              {food.scientific_source && (
                                <p className="text-xs text-[#4a5568] mt-2 flex items-center gap-1">
                                  <Beaker className="w-3 h-3" />
                                  {food.scientific_source}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* All Foods Education Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {myFoodsEducation.foods?.map((food, index) => (
                      <div
                        key={index}
                        className="animate-fade-in"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <Card className="border-none card-shadow h-full hover:shadow-lg transition-all">
                          {food.image && (
                            <div className="h-40 overflow-hidden rounded-t-xl">
                              <img 
                                src={food.image} 
                                alt={food.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <CardTitle className="font-serif text-lg text-[#1A4D2E]">
                                {food.name}
                              </CardTitle>
                              {food.from_your_plan && (
                                <Badge className="bg-[#9BCF53] text-white text-xs">Din planul tău</Badge>
                              )}
                            </div>
                            {food.relevant_for_condition && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs w-fit">
                                Recomandat pentru: {food.relevant_for_condition}
                              </Badge>
                            )}
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <div>
                                <p className="text-xs font-medium text-[#4a5568] mb-1">De ce este benefic:</p>
                                <ul className="text-sm text-[#4a5568] space-y-1">
                                  {food.benefits?.slice(0, 3).map((benefit, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                      <span className="text-[#9BCF53]">•</span>
                                      {benefit}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              
                              {food.conditions_helped?.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-[#4a5568] mb-1">Ajută la:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {food.conditions_helped.map((c, i) => (
                                      <Badge key={i} className="bg-[#1A4D2E]/10 text-[#1A4D2E] text-xs">{c}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {food.daily_recommended && (
                                <div className="p-2 bg-[#F5F5F0] rounded-lg">
                                  <p className="text-xs text-[#4a5568]">
                                    <strong>Doză recomandată:</strong> {food.daily_recommended}
                                  </p>
                                </div>
                              )}
                              
                              {food.scientific_source && (
                                <p className="text-xs text-[#4a5568] flex items-center gap-1 pt-2 border-t">
                                  <Beaker className="w-3 h-3" />
                                  Sursă: {food.scientific_source}
                                </p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Card className="border-none card-shadow">
                  <CardContent className="text-center py-12">
                    <Utensils className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                    <h3 className="font-serif text-xl text-[#1A4D2E] mb-2">
                      {myFoodsEducation?.message || 'Generează un plan alimentar'}
                    </h3>
                    <p className="text-[#4a5568] mb-4">
                      După ce vei genera un plan alimentar, vei vedea aici informații personalizate despre 
                      alimentele recomandate pentru tine și de ce sunt benefice pentru afecțiunile tale.
                    </p>
                    <Button 
                      onClick={() => window.location.href = '/meal-plans'}
                      className="bg-[#1A4D2E] text-white rounded-full"
                    >
                      Mergi la Planuri de Mese
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Articles Tab */}
            <TabsContent value="articles">
              {/* Category Filter */}
              <div className="flex flex-wrap gap-2 mb-6 justify-center">
                {categories.map((cat) => (
                  <Button
                    key={cat.id}
                    variant={selectedCategory === cat.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      fetchArticles(cat.id);
                    }}
                    className={selectedCategory === cat.id 
                      ? 'bg-[#1A4D2E] text-white' 
                      : 'border-[#1A4D2E]/30 text-[#1A4D2E]'}
                  >
                    <cat.icon className="w-4 h-4 mr-1" />
                    {cat.label}
                  </Button>
                ))}
              </div>

              {/* Articles Grid */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map((article) => (
                    <Card key={article.id} className="border-none card-shadow hover:shadow-lg transition-all overflow-hidden">
                      {article.image && (
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={article.image} 
                            alt={article.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between mb-2">
                          <Badge className="bg-[#1A4D2E]/10 text-[#1A4D2E] text-xs">
                            {article.category}
                          </Badge>
                          {article.read_time && (
                            <span className="text-xs text-[#4a5568] flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {article.read_time} min
                            </span>
                          )}
                        </div>
                        <CardTitle className="font-serif text-lg text-[#1A4D2E] line-clamp-2">
                          {article.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-[#4a5568] mb-3 line-clamp-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#4a5568]">
                            📚 {article.source}
                          </span>
                          <Button size="sm" variant="ghost" className="text-[#1A4D2E]">
                            Citește <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Recipes Tab */}
            <TabsContent value="recipes">
              {/* Search Form */}
              <Card className="border-none card-shadow mb-6">
                <CardContent className="p-6">
                  <form onSubmit={searchRecipes} className="space-y-4">
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          placeholder="Caută rețete (ex: salată, pui, supă)..."
                          value={recipeSearch}
                          onChange={(e) => setRecipeSearch(e.target.value)}
                          className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] h-12"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="bg-[#1A4D2E] text-white px-8"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    </div>
                    
                    {/* Filters */}
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-[#4a5568]" />
                        <span className="text-sm text-[#4a5568]">Dietă:</span>
                        <select
                          value={selectedDiet}
                          onChange={(e) => setSelectedDiet(e.target.value)}
                          className="text-sm bg-[#F5F5F0] border-none rounded-lg px-3 py-1"
                        >
                          {dietTypes.map((diet) => (
                            <option key={diet.id} value={diet.id}>{diet.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Recipes Grid */}
              {recipes.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {recipes.map((recipe, index) => (
                    <Card key={recipe.id || index} className="border-none card-shadow hover:shadow-lg transition-all overflow-hidden">
                      {recipe.image && (
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={recipe.image} 
                            alt={recipe.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardHeader className="pb-2">
                        <CardTitle className="font-serif text-lg text-[#1A4D2E] line-clamp-2">
                          {recipe.title}
                        </CardTitle>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {recipe.diet_labels?.slice(0, 3).map((label, i) => (
                            <Badge key={i} className="bg-[#9BCF53]/20 text-[#1A4D2E] text-xs">
                              {label}
                            </Badge>
                          ))}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-4 gap-2 text-center mb-4">
                          <div className="bg-[#F5F5F0] rounded-lg p-2">
                            <p className="text-lg font-bold text-[#1A4D2E]">{recipe.calories || '-'}</p>
                            <p className="text-xs text-[#4a5568]">kcal</p>
                          </div>
                          <div className="bg-[#F5F5F0] rounded-lg p-2">
                            <p className="text-lg font-bold text-[#1A4D2E]">{recipe.nutrients?.protein || '-'}</p>
                            <p className="text-xs text-[#4a5568]">proteine</p>
                          </div>
                          <div className="bg-[#F5F5F0] rounded-lg p-2">
                            <p className="text-lg font-bold text-[#1A4D2E]">{recipe.nutrients?.carbs || '-'}</p>
                            <p className="text-xs text-[#4a5568]">carbs</p>
                          </div>
                          <div className="bg-[#F5F5F0] rounded-lg p-2">
                            <p className="text-lg font-bold text-[#1A4D2E]">{recipe.nutrients?.fat || '-'}</p>
                            <p className="text-xs text-[#4a5568]">grăsimi</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#4a5568]">
                            {recipe.servings} porții • {recipe.database}
                          </span>
                          {recipe.source_url && (
                            <a 
                              href={recipe.source_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#1A4D2E] hover:underline text-sm flex items-center"
                            >
                              Vezi rețeta <ExternalLink className="w-3 h-3 ml-1" />
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-none card-shadow">
                  <CardContent className="p-12 text-center">
                    <Search className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                    <p className="text-[#4a5568]">Caută rețete din baze de date internaționale</p>
                    <p className="text-sm text-[#4a5568] mt-2">
                      Surse: USDA, Edamam, Spoonacular
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Conditions Tab */}
            <TabsContent value="conditions">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Condition Selector */}
                <Card className="border-none card-shadow">
                  <CardHeader>
                    <CardTitle className="font-serif text-2xl text-[#1A4D2E]">
                      Rețete pentru Afecțiuni
                    </CardTitle>
                    <CardDescription>
                      Rețete adaptate conform ghidurilor medicale internaționale
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {healthConditions.map((condition) => (
                        <Button
                          key={condition.id}
                          onClick={() => fetchRecipesByCondition(condition.id)}
                          variant={selectedCondition === condition.id ? 'default' : 'outline'}
                          className={`w-full justify-start h-auto py-4 ${
                            selectedCondition === condition.id 
                              ? 'bg-[#1A4D2E] text-white' 
                              : 'border-[#1A4D2E]/20'
                          }`}
                        >
                          <div className={`w-3 h-3 rounded-full ${condition.color} mr-3`} />
                          <span>{condition.label}</span>
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Guidelines */}
                {guidelines && (
                  <Card className="border-none card-shadow">
                    <CardHeader>
                      <CardTitle className="font-serif text-xl text-[#1A4D2E]">
                        Ghid Medical
                      </CardTitle>
                      <CardDescription>
                        Sursa: {guidelines.source}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-[#1A4D2E] mb-2">Recomandări:</h4>
                          <ul className="space-y-2">
                            {guidelines.recommendations?.map((rec, i) => (
                              <li key={i} className="flex items-start text-sm text-[#4a5568]">
                                <span className="text-[#9BCF53] mr-2">✓</span>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {guidelines.target_values && (
                          <div>
                            <h4 className="font-medium text-[#1A4D2E] mb-2">Valori Țintă:</h4>
                            <div className="space-y-1">
                              {Object.entries(guidelines.target_values).map(([key, value]) => (
                                <div key={key} className="flex justify-between text-sm">
                                  <span className="text-[#4a5568]">{key}:</span>
                                  <span className="font-medium text-[#1A4D2E]">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Condition Recipes */}
              {selectedCondition && recipes.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-serif text-xl text-[#1A4D2E] mb-4">
                    Rețete recomandate ({recipes.length})
                  </h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    {recipes.slice(0, 6).map((recipe, index) => (
                      <Card key={recipe.id || index} className="border-none card-shadow">
                        {recipe.image && (
                          <div className="h-40 overflow-hidden rounded-t-lg">
                            <img src={recipe.image} alt={recipe.title} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <h4 className="font-medium text-[#1A4D2E] line-clamp-2 mb-2">{recipe.title}</h4>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#4a5568]">{recipe.calories} kcal</span>
                            <span className="text-[#4a5568]">{recipe.nutrients?.carbs}g carbs</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Seasonal Tab */}
            <TabsContent value="seasonal">
              {seasonalData ? (
                <div className="grid md:grid-cols-2 gap-8">
                  <Card className="border-none card-shadow">
                    <CardHeader>
                      <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                        <Calendar className="w-6 h-6 mr-2" />
                        {seasonalData.month_name} - Ce e în Sezon
                      </CardTitle>
                      <CardDescription>
                        Ingrediente proaspete disponibile în România
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <h4 className="font-medium text-[#1A4D2E] mb-2 flex items-center">
                          🍎 Fructe
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {seasonalData.fruits?.map((fruit, i) => (
                            <Badge key={i} className="bg-red-100 text-red-800">{fruit}</Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-[#1A4D2E] mb-2 flex items-center">
                          🥬 Legume
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {seasonalData.vegetables?.map((veg, i) => (
                            <Badge key={i} className="bg-green-100 text-green-800">{veg}</Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-[#1A4D2E] mb-2 flex items-center">
                          🥩 Proteine
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {seasonalData.proteins?.map((protein, i) => (
                            <Badge key={i} className="bg-amber-100 text-amber-800">{protein}</Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none card-shadow">
                    <CardHeader>
                      <CardTitle className="font-serif text-xl text-[#1A4D2E]">
                        💡 Sfaturi pentru {seasonalData.month_name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-[#4a5568] mb-6">{seasonalData.tips}</p>
                      
                      <div className="bg-[#F5F5F0] rounded-xl p-4">
                        <h4 className="font-medium text-[#1A4D2E] mb-2">De ce să mănânci sezonier?</h4>
                        <ul className="space-y-2 text-sm text-[#4a5568]">
                          <li>✓ Conținut mai mare de nutrienți</li>
                          <li>✓ Gust și prospețime superioare</li>
                          <li>✓ Preț mai accesibil</li>
                          <li>✓ Impact ecologic redus</li>
                          <li>✓ Sprijini producătorii locali</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Sources Section */}
          <Card className="border-none card-shadow mt-12 max-w-4xl mx-auto">
            <CardContent className="p-8">
              <h3 className="font-serif text-xl text-[#1A4D2E] mb-4 text-center">
                Surse Științifice
              </h3>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div className="text-center">
                  <p className="font-medium text-[#1A4D2E]">🇺🇸 USDA FoodData Central</p>
                  <p className="text-[#4a5568]">Date nutriționale detaliate</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-[#1A4D2E]">🏥 NIH - NHLBI</p>
                  <p className="text-[#4a5568]">Dieta DASH, ghiduri cardiace</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-[#1A4D2E]">🌍 OMS / WHO</p>
                  <p className="text-[#4a5568]">Praguri sănătate globale</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-[#1A4D2E]">❤️ ESC</p>
                  <p className="text-[#4a5568]">Societatea Europeană Cardiologie</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-[#1A4D2E]">🇪🇺 EuroFIR</p>
                  <p className="text-[#4a5568]">Date nutriționale europene</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-[#1A4D2E]">🍽️ Edamam / Spoonacular</p>
                  <p className="text-[#4a5568]">Baze de date rețete</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Education;
